import { Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScStep, ScStepConfig } from '../../models';

@Injectable()
export class ScStepService {
  public isInitialized: boolean;
  private _steps: ScStep[] = [];
  private _router: Router;
  private _route: ActivatedRoute;
  private _form: FormGroup;
  private _formSubject: BehaviorSubject<FormGroup> = new BehaviorSubject(null);
  private _stepsSubject: BehaviorSubject<ScStep[]> = new BehaviorSubject([]);
  constructor() {}

  public get form(): FormGroup {
    return this._form;
  }

  public init(router: Router, route: ActivatedRoute, form: FormGroup) {
    this._route = route;
    this._router = router;
    this._form = form;
    this._formSubject.next(this.form);
  }

  public getSteps() {
    return this._stepsSubject.asObservable().pipe(filter(s => s.length > 0));
  }

  public getStepsStatic() {
    return this._steps;
  }

  public addSteps(steps: ScStepConfig[]) {
    this._setSteps(steps);
  }

  public removeSteps(steps: ScStepConfig[]) {}

  public getStepIndex(): number {
    return this._steps.indexOf(this.getStep());
  }

  public isLastStepIndex(): boolean {
    return this._steps.length - 1 === this.getStepIndex();
  }

  public getStep(route: string = this._defaultRoutePath()): ScStep {
    if (!this._steps.length) {
      return null;
    }
    return this._steps.find(v => v.route === route) || this._steps[0];
  }

  public getNextStep(): ScStep {
    return this._steps[this.getStepIndex() + 1];
  }

  public goNextStep(): void {
    const next = this.getNextStep();
    const current = this.getStep();
    current.done = true;
    this._router
      .navigate([next.route], {
        relativeTo: this._route,
        replaceUrl: true
      })
      .then(() => {});
  }

  public goPreviousStep(): void {
    const previous = this.getPreviousStep();
    this._router.navigate([previous.route], {
      relativeTo: this._route,
      replaceUrl: true
    });
  }

  public getPreviousStep(): ScStep {
    return this._steps[this.getStepIndex() - 1];
  }

  public reset() {
    this._steps = [];
    this._router = null;
    this._route = null;
    this.isInitialized = false;
    this._formSubject = new BehaviorSubject(null);
    if (this.form) {
      this.form.reset();
    }
    this._stepsSubject = new BehaviorSubject([]);
  }

  private _defaultRoutePath() {
    if (!this._route) {
      return null;
    }
    const activeChild = this._route.firstChild;

    if (!activeChild) {
      return null;
    }
    return activeChild.routeConfig.path;
  }

  private _setSteps(steps: ScStepConfig[]) {
    this._formSubject
      .asObservable()
      .pipe(filter(form => !!form))
      .subscribe(() => {
        const newSteps = steps.filter(s => !this._steps.some(n => n.route === s.route));

        this._steps = [
          ...this._steps,
          ...newSteps.map((step, index) => {
            const groupName = step['config'] ? step['config'].formGroupName : step.formGroupName;

            let form = this.form.get(groupName) as FormGroup;

            if (!form) {
              form = new FormGroup({}, Validators.required);
              this.form.addControl(groupName, form);
            }

            if (step['config']) {
              return new ScStep({
                ...(step as any),
                form
              });
            }

            const astep = {
              config: step,
              stepIndex: this._steps.length + index,
              form,
              route: step.route,
              done: step['done'] || false
            };

            return new ScStep(astep);
          })
        ];
        this._stepsSubject.next(this._steps);
      });
  }
}
